/**
 * Fits4Kids — Delivery countdown
 *
 * Logica:
 * - NL/BE: cutoff 23:00, volgende werkdag in huis (vrijdag → zaterdag, weekend → dinsdag)
 * - EU: cutoff 22:00, 2-4 werkdagen na verzendmoment
 * - Skip weekends + NL feestdagen voor verzendmoment
 * - Update countdown elke seconde, datum elke minuut
 *
 * Bevat geen externe dependencies.
 */
(function () {
  'use strict';

  const NLBE = ['NL', 'BE'];

  function isWeekend(date) {
    const d = date.getDay();
    return d === 0 || d === 6;
  }

  function toIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function isHoliday(date, holidays) {
    return holidays.indexOf(toIso(date)) !== -1;
  }

  function isShippingDay(date, holidays) {
    return !isWeekend(date) && !isHoliday(date, holidays);
  }

  /**
   * Volgende moment waarop pakket de deur uit gaat.
   * Als nu een werkdag is voor cutoff → vandaag
   * Anders → eerstvolgende werkdag
   */
  function getNextShipDate(now, holidays, cutoffHour) {
    const cutoffToday = new Date(now);
    cutoffToday.setHours(cutoffHour, 0, 0, 0);

    if (isShippingDay(now, holidays) && now < cutoffToday) {
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }
    const ship = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    while (!isShippingDay(ship, holidays)) {
      ship.setDate(ship.getDate() + 1);
    }
    return ship;
  }

  /**
   * NL/BE delivery: ship + 1 dag.
   * PostNL/BPost bezorgen ook op zaterdag, dus vrijdag → zaterdag werkt.
   * Maandag ship → dinsdag delivery. Etc.
   */
  function getDeliveryDateNLBE(shipDate) {
    const delivery = new Date(shipDate);
    delivery.setDate(delivery.getDate() + 1);
    // Als de bezorgdatum op zondag valt (theoretisch), schuif naar maandag
    if (delivery.getDay() === 0) {
      delivery.setDate(delivery.getDate() + 1);
    }
    return delivery;
  }

  /**
   * EU delivery: 2-4 werkdagen na ship date.
   * We tellen werkdagen (geen weekend, geen NL feestdag — andere EU feestdagen negeren we).
   */
  function getDeliveryRangeEU(shipDate, holidays) {
    function addWorkdays(start, n) {
      const d = new Date(start);
      let added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        if (isShippingDay(d, holidays)) added++;
      }
      return d;
    }
    return {
      min: addWorkdays(shipDate, 2),
      max: addWorkdays(shipDate, 4)
    };
  }

  function formatDate(date, locale) {
    const opts = { weekday: 'long', day: 'numeric', month: 'long' };
    try {
      return new Intl.DateTimeFormat(locale, opts).format(date);
    } catch (e) {
      return new Intl.DateTimeFormat('nl-NL', opts).format(date);
    }
  }

  function formatDateShort(date, locale) {
    const opts = { day: 'numeric', month: 'long' };
    try {
      return new Intl.DateTimeFormat(locale, opts).format(date);
    } catch (e) {
      return new Intl.DateTimeFormat('nl-NL', opts).format(date);
    }
  }

  function render(el) {
    const country = (el.dataset.country || 'NL').toUpperCase();
    const locale = el.dataset.locale || 'nl';
    const cutoffHour = NLBE.indexOf(country) !== -1
      ? parseInt(el.dataset.cutoffNlbe || '23', 10)
      : parseInt(el.dataset.cutoffEu || '22', 10);
    const holidays = (el.dataset.holidays || '').split(',').filter(Boolean);

    const txt = {
      countdownPre: el.dataset.textCountdownPre || '',
      countdownPost: el.dataset.textCountdownPost || '',
      countdownPassed: el.dataset.textCountdownPassed || '',
      hoursShort: el.dataset.textHoursShort || 'u',
      minutesShort: el.dataset.textMinutesShort || 'min',
      deliveryNlbe: el.dataset.textDeliveryNlbe || '',
      deliveryEu: el.dataset.textDeliveryEu || ''
    };

    const countdownEl = el.querySelector('[data-countdown-text]');
    const dateEl = el.querySelector('[data-date-text]');

    const now = new Date();
    const cutoffToday = new Date(now);
    cutoffToday.setHours(cutoffHour, 0, 0, 0);

    const todayIsShippingDay = isShippingDay(now, holidays);
    const beforeCutoff = now < cutoffToday;

    // --- Countdown row ---
    if (todayIsShippingDay && beforeCutoff) {
      const diffMs = cutoffToday - now;
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      const timeStr = `<strong>${hours}${txt.hoursShort} ${minutes}${txt.minutesShort}</strong>`;
      countdownEl.innerHTML = `${txt.countdownPre} ${timeStr} ${txt.countdownPost}`.trim();
      el.classList.remove('f4k-delivery--passed');
    } else {
      const nextShip = getNextShipDate(now, holidays, cutoffHour);
      const dateStr = `<strong>${formatDate(nextShip, locale)}</strong>`;
      countdownEl.innerHTML = txt.countdownPassed.replace('{date}', dateStr);
      el.classList.add('f4k-delivery--passed');
    }

    // --- Delivery date row ---
    const shipDate = getNextShipDate(now, holidays, cutoffHour);
    if (NLBE.indexOf(country) !== -1) {
      const delivery = getDeliveryDateNLBE(shipDate);
      const dateStr = `<strong>${formatDate(delivery, locale)}</strong>`;
      dateEl.innerHTML = txt.deliveryNlbe.replace('{date}', dateStr);
    } else {
      const range = getDeliveryRangeEU(shipDate, holidays);
      const d1 = `<strong>${formatDateShort(range.min, locale)}</strong>`;
      const d2 = `<strong>${formatDateShort(range.max, locale)}</strong>`;
      dateEl.innerHTML = txt.deliveryEu.replace('{date1}', d1).replace('{date2}', d2);
    }
  }

  function init() {
    const els = document.querySelectorAll('[data-f4k-delivery]');
    els.forEach(function (el) {
      if (el.__f4kInit) return;
      el.__f4kInit = true;
      render(el);
      // Update countdown elke 30s (uren+minuten zijn voldoende fijn)
      el.__f4kTimer = setInterval(function () { render(el); }, 30000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init wanneer de cart drawer opent / cart wordt geüpdatet
  // Broadcast triggert deze events bij drawer/cart wijzigingen
  ['cart:updated', 'cart:open', 'cart:refresh', 'shopify:section:load'].forEach(function (ev) {
    document.addEventListener(ev, function () { setTimeout(init, 50); });
  });
})();